import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { curveMonotoneX, line as d3Line } from 'd3-shape';

import { DataOfferCatalogDetailDto } from '../../../../models/bautagebuch.models';
import { DataspaceDataSectionVm } from '../../../../models/dataspace-workspace.models';
import { buildDataspaceSections, prettyDataspaceSectionTitle } from '../../../../utils/dataspace-section-summary.util';

type CombinedChartAxis = 'left' | 'right';

interface CombinedChartPoint {
  label: string;
  value: number;
  x: number;
  y: number;
}

interface CombinedChartSeries {
  key: string;
  label: string;
  color: string;
  axis: CombinedChartAxis;
  visible: boolean;
  values: Array<number | null>;
  points: CombinedChartPoint[];
  path: string;
}

interface CombinedChartHoverState {
  seriesLabel: string;
  pointLabel: string;
  value: number;
  x: number;
  y: number;
  color: string;
}

@Component({
  standalone: false,
  selector: 'app-gu-data-access',
  templateUrl: './gu-data-access.component.html',
  styleUrls: ['./gu-data-access.component.scss']
})
export class GuDataAccessComponent implements OnChanges {
  @Input() detail: DataOfferCatalogDetailDto | null = null;
  selectedTabIndex = 0;

  reports: Array<{
    reportId: string;
    label: string;
    status: string;
    reportDate: string | null;
    sections: DataspaceDataSectionVm[];
    payload: { [key: string]: any };
  }> = [];
  expandedReportId: string | null = null;
  expandedSectionKey: string | null = null;

  deepSearchTerm = '';
  deepSearchSectionFilters: Set<string> = new Set();
  deepSearchResults: Array<{
    reportId: string;
    label: string;
    reportDate: string | null;
    sectionId: string;
    sectionTitle: string;
    matchContext: string;
    payload: any;
  }> = [];
  deepSearchExecuted = false;
  expandedDeepResultKey: string | null = null;
  deepSearchExamples: Array<{ term: string; hint: string }> = [];

  aggregatedWeather: Array<{ name: string; series: Array<{ name: string; value: number }> }> = [];
  aggregatedWorkerTimeline: Array<{ name: string; series: Array<{ name: string; value: number }> }> = [];
  aggregatedWorkHoursTimeline: Array<{ name: string; series: Array<{ name: string; value: number }> }> = [];
  aggregatedBadWeatherTimeline: Array<{ name: string; series: Array<{ name: string; value: number }> }> = [];

  readonly combinedChartWidth = 820;
  readonly combinedChartHeight = 360;
  readonly combinedChartPadding = { top: 28, right: 142, bottom: 56, left: 82 };
  combinedChartXAxisLabels: Array<{ label: string; x: number }> = [];
  combinedChartGridLines: Array<{ y: number }> = [];
  combinedChartLeftTicks: Array<{ value: number; label: string; y: number }> = [];
  combinedChartRightTicks: Array<{ value: number; label: string; y: number }> = [];
  combinedChartSeries: CombinedChartSeries[] = [];
  hoveredChartPoint: CombinedChartHoverState | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.detail) {
      this.selectedTabIndex = 0;
      this.loadReportData();
    }
  }

  toggleReport(reportId: string): void {
    this.expandedReportId = this.expandedReportId === reportId ? null : reportId;
  }

  toggleSection(reportId: string, sectionId: string): void {
    const key = `${reportId}:${sectionId}`;
    this.expandedSectionKey = this.expandedSectionKey === key ? null : key;
  }

  isSectionExpanded(reportId: string, sectionId: string): boolean {
    return this.expandedSectionKey === `${reportId}:${sectionId}`;
  }

  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  isObject(value: any): boolean {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  isPrimitive(value: any): boolean {
    return value === null || value === undefined || typeof value !== 'object';
  }

  objectKeys(obj: any): string[] {
    if (!obj || typeof obj !== 'object') {
      return [];
    }
    return Object.keys(obj).filter((k) => k !== 'selectedSectionIds');
  }

  formatLabel(key: string): string {
    return key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^./, (m) => m.toUpperCase());
  }

  formatValue(val: any): string {
    if (val === null || val === undefined) {
      return '–';
    }
    if (typeof val === 'boolean') {
      return val ? 'Ja' : 'Nein';
    }
    return String(val);
  }

  formatInlineValue(val: any): string {
    if (this.isPrimitive(val)) {
      return this.formatValue(val);
    }
    if (Array.isArray(val)) {
      const formatted = val
        .map((entry) => this.formatInlineValue(entry))
        .filter((entry) => entry && entry !== '–');
      return formatted.length ? formatted.join('; ') : '–';
    }

    const pairs = this.objectKeys(val)
      .map((key) => `${this.formatLabel(key)}: ${this.formatInlineValue(val[key])}`)
      .filter((entry) => entry && !entry.endsWith(': –'));
    return pairs.length ? pairs.join(' | ') : '–';
  }

  getArrayColumns(arr: any[]): string[] {
    if (!arr?.length) {
      return [];
    }
    const first = arr.find((item) => item && typeof item === 'object' && !Array.isArray(item));
    if (!first) {
      return [];
    }
    return Object.keys(first).filter((k) => k !== 'selectedSectionIds');
  }

  get availableSearchSections(): string[] {
    return this.detail?.includedSections || [];
  }

  get hasSecuredViewerAccess(): boolean {
    return !!this.detail?.contentAccessible;
  }

  get hasComputeToDataAccess(): boolean {
    return this.detail?.usagePolicy?.mode === 'compute-to-data';
  }

  get hasDeepSearchAccess(): boolean {
    return this.hasSecuredViewerAccess && this.reports.length > 0;
  }

  prettySection(sectionId: string): string {
    return prettyDataspaceSectionTitle(sectionId);
  }

  toggleSearchSection(sectionId: string): void {
    const nextFilters = new Set(this.deepSearchSectionFilters);
    if (nextFilters.has(sectionId)) {
      nextFilters.delete(sectionId);
    } else {
      nextFilters.add(sectionId);
    }
    this.deepSearchSectionFilters = nextFilters;
    if (this.deepSearchExecuted) {
      this.executeDeepSearch();
    }
  }

  selectAllSearchSections(): void {
    this.deepSearchSectionFilters = new Set(this.availableSearchSections);
    if (this.deepSearchExecuted) {
      this.executeDeepSearch();
    }
  }

  clearSearchSections(): void {
    this.deepSearchSectionFilters = new Set();
    if (this.deepSearchExecuted) {
      this.executeDeepSearch();
    }
  }

  isSectionFilterActive(sectionId: string): boolean {
    return this.deepSearchSectionFilters.has(sectionId);
  }

  executeDeepSearch(): void {
    this.deepSearchResults = [];
    this.deepSearchExecuted = true;

    const term = this.deepSearchTerm.trim();
    if (!term) {
      this.deepSearchExecuted = false;
      return;
    }

    const lowerTerm = term.toLowerCase();
    if (this.availableSearchSections.length > 0 && this.deepSearchSectionFilters.size === 0) {
      return;
    }

    for (const report of this.reports) {
      const sectionKeys = Object.keys(report.payload).filter((k) => k !== 'selectedSectionIds');
      for (const sectionId of sectionKeys) {
        if (!this.deepSearchSectionFilters.has(sectionId)) {
          continue;
        }
        const sectionValue = report.payload[sectionId];
        const serialized = JSON.stringify(sectionValue);
        const lowerSerialized = serialized.toLowerCase();
        const matchIdx = lowerSerialized.indexOf(lowerTerm);
        if (matchIdx === -1) {
          continue;
        }
        const snippetStart = Math.max(0, matchIdx - 40);
        const snippetEnd = Math.min(serialized.length, matchIdx + term.length + 40);
        const snippet =
          (snippetStart > 0 ? '…' : '') +
          serialized.substring(snippetStart, snippetEnd) +
          (snippetEnd < serialized.length ? '…' : '');

        this.deepSearchResults.push({
          reportId: report.reportId,
          label: report.label,
          reportDate: report.reportDate,
          sectionId,
          sectionTitle: prettyDataspaceSectionTitle(sectionId),
          matchContext: snippet,
          payload: sectionValue
        });
      }
    }
  }

  applyExample(term: string): void {
    this.deepSearchTerm = term;
    this.selectAllSearchSections();
    this.executeDeepSearch();
  }

  toggleDeepResult(reportId: string, sectionId: string): void {
    const key = `${reportId}:${sectionId}`;
    this.expandedDeepResultKey = this.expandedDeepResultKey === key ? null : key;
  }

  isDeepResultExpanded(reportId: string, sectionId: string): boolean {
    return this.expandedDeepResultKey === `${reportId}:${sectionId}`;
  }

  get deepSearchReportCount(): number {
    const ids = new Set(this.deepSearchResults.map((r) => r.reportId));
    return ids.size;
  }

  get hasCombinedChartData(): boolean {
    return this.combinedChartSeries.length > 0;
  }

  get visibleCombinedChartSeries(): CombinedChartSeries[] {
    return this.combinedChartSeries.filter((series) => series.visible && series.points.length > 0);
  }

  get combinedChartPlotWidth(): number {
    return this.combinedChartWidth - this.combinedChartPadding.left - this.combinedChartPadding.right;
  }

  get combinedChartPlotHeight(): number {
    return this.combinedChartHeight - this.combinedChartPadding.top - this.combinedChartPadding.bottom;
  }

  get combinedChartBottom(): number {
    return this.combinedChartHeight - this.combinedChartPadding.bottom;
  }

  toggleChartSeries(seriesKey: string): void {
    this.combinedChartSeries = this.combinedChartSeries.map((series) =>
      series.key === seriesKey ? { ...series, visible: !series.visible } : series
    );
    this.hoveredChartPoint = null;
    this.updateCombinedChartGeometry();
  }

  showChartPointTooltip(series: CombinedChartSeries, point: CombinedChartPoint): void {
    this.hoveredChartPoint = {
      seriesLabel: series.label,
      pointLabel: point.label,
      value: point.value,
      x: point.x,
      y: point.y,
      color: series.color
    };
  }

  hideChartPointTooltip(): void {
    this.hoveredChartPoint = null;
  }

  private loadReportData(): void {
    this.reports = [];
    this.deepSearchSectionFilters = new Set(this.availableSearchSections);

    if (!this.detail?.contentAccessible || !this.detail?.resolvedReports?.length) {
      this.expandedReportId = null;
      this.expandedSectionKey = null;
      this.clearAggregations();
      return;
    }

    this.reports = this.detail.resolvedReports.map((report) => ({
      reportId: report.reportId,
      label: report.reportDate || report.projectLabel || 'Bericht',
      status: report.reportLifecycleStatus,
      reportDate: report.reportDate || null,
      payload: report.reportPayload || {},
      sections: buildDataspaceSections(
        report.reportPayload || {},
        this.detail?.includedSections || Object.keys(report.reportPayload || {})
      )
    }));

    this.expandedReportId = this.reports[0]?.reportId || null;
    const firstSection = this.reports[0]?.sections[0]?.id;
    this.expandedSectionKey =
      this.expandedReportId && firstSection ? `${this.expandedReportId}:${firstSection}` : null;

    this.buildAggregations();
    this.buildSearchExamples();
  }

  private buildSearchExamples(): void {
    const skipSections = new Set(['selectedSectionIds', 'project', 'day']);
    const seen = new Set<string>();
    const examples: Array<{ term: string; hint: string }> = [];

    for (const report of this.reports) {
      if (examples.length >= 3) {
        break;
      }
      const sectionKeys = Object.keys(report.payload).filter((k) => !skipSections.has(k));
      for (const sectionId of sectionKeys) {
        if (examples.length >= 3) {
          break;
        }
        const term = this.extractExampleTerm(report.payload[sectionId]);
        if (term && !seen.has(term.toLowerCase())) {
          seen.add(term.toLowerCase());
          examples.push({ term, hint: prettyDataspaceSectionTitle(sectionId) });
        }
      }
    }

    this.deepSearchExamples = examples;
  }

  private extractExampleTerm(value: any): string | null {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item && typeof item === 'object') {
          for (const field of ['bezeichnung', 'beschreibung', 'materialOderBauteil', 'taetigkeit', 'art']) {
            const candidate = this.sanitizeExampleTerm(item[field]);
            if (candidate) {
              return candidate;
            }
          }
          for (const key of Object.keys(item)) {
            const candidate = this.sanitizeExampleTerm(item[key]);
            if (candidate) {
              return candidate;
            }
          }
        }
      }
      return null;
    }

    if (value && typeof value === 'object') {
      for (const key of Object.keys(value)) {
        if (key === 'selectedSectionIds') {
          continue;
        }
        const candidate = this.sanitizeExampleTerm(value[key]);
        if (candidate) {
          return candidate;
        }
        if (Array.isArray(value[key])) {
          const nested = this.extractExampleTerm(value[key]);
          if (nested) {
            return nested;
          }
        }
      }
    }

    return null;
  }

  private sanitizeExampleTerm(raw: any): string | null {
    if (typeof raw !== 'string' || raw.length < 4) {
      return null;
    }
    if (/^\d{4}-\d{2}-\d{2}|^\d{2}:\d{2}/.test(raw)) {
      return null;
    }
    const trimmed = raw.trim();
    if (trimmed.length <= 20) {
      return trimmed;
    }
    const cut = trimmed.substring(0, 20);
    const lastSpace = cut.lastIndexOf(' ');
    return lastSpace > 4 ? cut.substring(0, lastSpace) : cut;
  }

  private buildAggregations(): void {
    const sorted = [...this.reports].sort((a, b) => {
      if (a.reportDate && b.reportDate) {
        return a.reportDate > b.reportDate ? 1 : -1;
      }
      return 0;
    });

    const minSeries: Array<{ name: string; value: number }> = [];
    const maxSeries: Array<{ name: string; value: number }> = [];
    sorted.forEach((report, index) => {
      const weather = report.payload?.weather;
      const dateLabel = this.axisLabel(report, index);
      if (weather) {
        if (weather.temperaturMinC !== undefined && weather.temperaturMinC !== null && weather.temperaturMinC !== '') {
          minSeries.push({ name: dateLabel, value: Number(weather.temperaturMinC) });
        }
        if (weather.temperaturMaxC !== undefined && weather.temperaturMaxC !== null && weather.temperaturMaxC !== '') {
          maxSeries.push({ name: dateLabel, value: Number(weather.temperaturMaxC) });
        }
      }
    });
    this.aggregatedWeather = [];
    if (minSeries.length) {
      this.aggregatedWeather.push({ name: 'Min °C', series: minSeries });
    }
    if (maxSeries.length) {
      this.aggregatedWeather.push({ name: 'Max °C', series: maxSeries });
    }

    const workerSeries: Array<{ name: string; value: number }> = [];
    sorted.forEach((report, index) => {
      const workers = report.payload?.nuFirma?.arbeitskraefte;
      if (Array.isArray(workers)) {
        workerSeries.push({ name: this.axisLabel(report, index), value: workers.length });
      }
    });
    this.aggregatedWorkerTimeline = workerSeries.length ? [{ name: 'Arbeitskräfte', series: workerSeries }] : [];

    const hoursSeries: Array<{ name: string; value: number }> = [];
    sorted.forEach((report, index) => {
      const day = report.payload?.day;
      if (day?.arbeitszeit?.beginn && day?.arbeitszeit?.ende) {
        const hours = this.parseWorkHours(day.arbeitszeit.beginn, day.arbeitszeit.ende);
        if (hours > 0) {
          hoursSeries.push({ name: this.axisLabel(report, index), value: hours });
        }
      }
    });
    this.aggregatedWorkHoursTimeline = hoursSeries.length ? [{ name: 'Arbeitsstunden', series: hoursSeries }] : [];

    const badWeatherSeries: Array<{ name: string; value: number }> = [];
    sorted.forEach((report, index) => {
      const badWeather = report.payload?.nuFirma?.schlechtesWetter;
      const hours = this.parseWorkHours(badWeather?.beginn || '', badWeather?.ende || '');
      badWeatherSeries.push({ name: this.axisLabel(report, index), value: hours });
    });
    this.aggregatedBadWeatherTimeline = badWeatherSeries.length ? [{ name: 'Schlechtes Wetter', series: badWeatherSeries }] : [];

    this.buildCombinedChart(sorted.map((report, index) => this.axisLabel(report, index)));
  }

  private axisLabel(report: { reportDate: string | null; label: string }, index: number): string {
    if (report.reportDate) {
      return this.formatDateForAxis(report.reportDate);
    }
    return report.label || `Bericht ${index + 1}`;
  }

  private clearAggregations(): void {
    this.aggregatedWeather = [];
    this.aggregatedWorkerTimeline = [];
    this.aggregatedWorkHoursTimeline = [];
    this.aggregatedBadWeatherTimeline = [];
    this.combinedChartXAxisLabels = [];
    this.combinedChartGridLines = [];
    this.combinedChartLeftTicks = [];
    this.combinedChartRightTicks = [];
    this.combinedChartSeries = [];
  }

  private buildCombinedChart(labels: string[]): void {
    const previousVisibility = new Map(this.combinedChartSeries.map((series) => [series.key, series.visible]));

    this.combinedChartSeries = [
      this.createCombinedSeries('tempMin', 'Min °C', 'var(--app-primary)', 'left', this.seriesValuesByLabel(this.aggregatedWeather, 'Min °C', labels), previousVisibility),
      this.createCombinedSeries('tempMax', 'Max °C', 'var(--app-warn)', 'left', this.seriesValuesByLabel(this.aggregatedWeather, 'Max °C', labels), previousVisibility),
      this.createCombinedSeries('workers', 'Arbeitskräfte', 'var(--app-primary)', 'right', this.seriesValuesByLabel(this.aggregatedWorkerTimeline, 'Arbeitskräfte', labels), previousVisibility),
      this.createCombinedSeries('hours', 'Arbeitsstunden', 'var(--app-accent)', 'right', this.seriesValuesByLabel(this.aggregatedWorkHoursTimeline, 'Arbeitsstunden', labels), previousVisibility),
      this.createCombinedSeries('badWeather', 'Schlechtes Wetter', 'var(--app-primary)', 'right', this.seriesValuesByLabel(this.aggregatedBadWeatherTimeline, 'Schlechtes Wetter', labels), previousVisibility)
    ].filter((series) => series.values.some((value) => value !== null));

    this.updateCombinedChartGeometry(labels);
  }

  private createCombinedSeries(
    key: string,
    label: string,
    color: string,
    axis: CombinedChartAxis,
    values: Array<number | null>,
    previousVisibility: Map<string, boolean>
  ): CombinedChartSeries {
    return {
      key,
      label,
      color,
      axis,
      visible: previousVisibility.get(key) ?? true,
      values,
      points: [],
      path: ''
    };
  }

  private seriesValuesByLabel(
    source: Array<{ name: string; series: Array<{ name: string; value: number }> }>,
    seriesLabel: string,
    labels: string[]
  ): Array<number | null> {
    const series = source.find((entry) => entry.name === seriesLabel)?.series || [];
    const valuesByLabel = new Map(series.map((entry) => [entry.name, entry.value]));
    return labels.map((label) => {
      const value = valuesByLabel.get(label);
      return value === undefined || Number.isNaN(value) ? null : value;
    });
  }

  private updateCombinedChartGeometry(labels: string[] = this.combinedChartXAxisLabels.map((entry) => entry.label)): void {
    if (!labels.length || !this.combinedChartSeries.length) {
      this.combinedChartXAxisLabels = [];
      this.combinedChartGridLines = [];
      this.combinedChartLeftTicks = [];
      this.combinedChartRightTicks = [];
      this.combinedChartSeries = this.combinedChartSeries.map((series) => ({ ...series, points: [], path: '' }));
      return;
    }

    const leftRange = this.computeAxisRange(this.combinedChartSeries, 'left');
    const rightRange = this.computeAxisRange(this.combinedChartSeries, 'right');
    const stepX = labels.length > 1 ? this.combinedChartPlotWidth / (labels.length - 1) : 0;

    this.combinedChartXAxisLabels = labels.map((label, index) => ({
      label,
      x: labels.length > 1 ? this.combinedChartPadding.left + index * stepX : this.combinedChartPadding.left + this.combinedChartPlotWidth / 2
    }));

    this.combinedChartLeftTicks = this.buildAxisTicks(leftRange.min, leftRange.max, 'left').map((value) => ({
      value,
      label: this.formatAxisTick(value, 'left'),
      y: this.valueToY(value, leftRange.min, leftRange.max)
    }));

    this.combinedChartRightTicks = this.buildAxisTicks(rightRange.min, rightRange.max, 'right').map((value) => ({
      value,
      label: this.formatAxisTick(value, 'right'),
      y: this.valueToY(value, rightRange.min, rightRange.max)
    }));

    this.combinedChartGridLines = this.combinedChartLeftTicks.map((tick) => ({ y: tick.y }));

    this.combinedChartSeries = this.combinedChartSeries.map((series) => {
      const axisRange = series.axis === 'left' ? leftRange : rightRange;
      const points = series.values
        .map((value, index) => {
          if (value === null) {
            return null;
          }
          return {
            label: labels[index],
            value,
            x: labels.length > 1 ? this.combinedChartPadding.left + index * stepX : this.combinedChartPadding.left + this.combinedChartPlotWidth / 2,
            y: this.valueToY(value, axisRange.min, axisRange.max)
          };
        })
        .filter((point): point is CombinedChartPoint => point !== null);

      const path = points.length >= 2
        ? d3Line<CombinedChartPoint>().x((point) => point.x).y((point) => point.y).curve(curveMonotoneX)(points) || ''
        : '';

      return { ...series, points, path };
    });
  }

  private computeAxisRange(seriesList: CombinedChartSeries[], axis: CombinedChartAxis): { min: number; max: number } {
    const values = seriesList
      .filter((series) => series.axis === axis && series.visible)
      .reduce<Array<number | null>>((allValues, series) => allValues.concat(series.values), [])
      .filter((value): value is number => value !== null);

    if (!values.length) {
      return axis === 'left' ? { min: 0, max: 10 } : { min: 0, max: 5 };
    }

    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);

    if (axis === 'left') {
      const padding = Math.max(1, (rawMax - rawMin) * 0.15);
      return { min: Math.floor(rawMin - padding), max: Math.ceil(rawMax + padding) };
    }

    const integerStep = Math.max(1, Math.ceil(rawMax / 4));
    const integerMax = integerStep * 4;
    return { min: 0, max: integerMax };
  }

  private buildAxisTicks(min: number, max: number, axis: CombinedChartAxis, tickCount = 5): number[] {
    if (max <= min) {
      return [min];
    }

    if (axis === 'right') {
      const integerStep = Math.max(1, Math.round((max - min) / (tickCount - 1)));
      return Array.from({ length: tickCount }, (_, index) => max - index * integerStep);
    }

    const ticks: number[] = [];
    for (let index = 0; index < tickCount; index += 1) {
      const ratio = index / (tickCount - 1);
      const value = min + (max - min) * ratio;
      ticks.push(Math.round(value * 10) / 10);
    }
    return ticks.reverse();
  }

  private valueToY(value: number, min: number, max: number): number {
    if (max <= min) {
      return this.combinedChartBottom;
    }
    const ratio = (value - min) / (max - min);
    return this.combinedChartBottom - ratio * this.combinedChartPlotHeight;
  }

  private formatAxisTick(value: number, axis: CombinedChartAxis): string {
    if (axis === 'left') {
      return `${value.toFixed(0)}°`;
    }
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  }

  private parseWorkHours(beginn: string, ende: string): number {
    const start = this.parseTime(beginn);
    const end = this.parseTime(ende);
    if (start === null || end === null) {
      return 0;
    }
    const delta = end - start;
    return delta > 0 ? Math.round(delta * 10) / 10 : 0;
  }

  private parseTime(value: string): number | null {
    if (!value) {
      return null;
    }
    const parts = value.split(':');
    if (parts.length < 2) {
      return null;
    }
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) {
      return null;
    }
    return h + m / 60;
  }

  private formatDateForAxis(dateStr: string): string {
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const [year, month, day] = dateStr.substring(0, 10).split('-');
      return `${day}.${month}.${year}`;
    }
    return dateStr;
  }
}
